import React, { useEffect, useState } from 'react';
import get from 'lodash/get';
import { matchPath } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  closeExploreGroup,
  removeFromManualGroup,
  deleteManualGroups,
  hideUngroupConfirmationModal,
  showUngroupConfirmationModal,
  setExploreGroupActiveTab,
  refreshExploreGroupHosts,
  refreshExploreGroupServices
} from 'store/GroupVulns/actions';
import {
  selectExploreGroupActiveTab,
  selectExploreGroupPayload,
  selectExploreGroupHostsCount,
  selectExploreGroupServicesCount,
  selectIsExploreGroupHostsFetching,
  selectIsExploreGroupServicesFetching,
  selectShowUngroupConfirmationModal,
  selectIsExploreGroupOpen
} from 'store/GroupVulns/selectors';
import { unSelectAll } from 'store/Manage/actions';
import {
  selectIsFetching as selectIsManageFetching,
  selectVulnsCount as selectManageVulnsCount,
  selectVulnsSelected
} from 'store/Manage/selectors';
import { redirect } from 'store/Router/actions';
import { selectPathname } from 'store/Router/selectors';
import {
  Wrapper,
  Left,
  FolderIcon,
  Title,
  WrapperButton,
  Prev,
  IconVulnerability,
  IconAsset,
  IconService,
  Ungroup
} from './styled';
import { Separator } from 'Common/Components/CustomActionBar/styled';
import GroupBadge from './components/GroupBadge';
import ExploreTabs from './components/ExploreTabs';
import UngroupConfirmationModal from './components/UngroupConfirmationModal';

const GROUP_BY_LABELS = {
  name: 'NAME',
  cve: 'CVE',
  cve_instances__name: 'CVE',
  external_id: 'EXTERNAL ID'
};

const getVulnId = (vuln) => vuln?._id ?? vuln?.id ?? null;

const belongsToGroup = (vuln, groupId) => {
  const vulnId = getVulnId(vuln);
  const groupKey = vuln?.group_id ?? vuln?.group_vuln_id ?? (vuln?.is_main === true ? vulnId : null);

  if (groupKey === null || typeof groupKey === 'undefined') return false;
  return String(groupKey) === String(groupId);
};

const Button = ({
  icon, tooltip, onClick, disabled, small
}) => <WrapperButton type="button" title={ tooltip } onClick={ onClick } disabled={ disabled } small={ small }>{ icon }</WrapperButton>;

const ExploreGroupBar = () => {
  const dispatch = useDispatch();
  const [isSubmittingUngroup, setIsSubmittingUngroup] = useState(false);
  const activeTab = useSelector(selectExploreGroupActiveTab);
  const isOpen = useSelector(selectIsExploreGroupOpen);
  const explorePayload = useSelector(selectExploreGroupPayload);
  const manageVulnCount = useSelector(selectManageVulnsCount);
  const isManageFetching = useSelector(selectIsManageFetching);
  const rawAssetsCount = useSelector(selectExploreGroupHostsCount);
  const rawServicesCount = useSelector(selectExploreGroupServicesCount);
  const isHostsFetching = useSelector(selectIsExploreGroupHostsFetching);
  const isServicesFetching = useSelector(selectIsExploreGroupServicesFetching);
  const showUngroupModal = useSelector(selectShowUngroupConfirmationModal);
  const vulnsSelected = useSelector(selectVulnsSelected);
  const pathname = useSelector(selectPathname);

  const exploreRouteMatch = matchPath(pathname, {
    path: '/vulns/:ws/groups/:groupId',
    exact: true,
    strict: false
  });
  const isExploreRoute = Boolean(exploreRouteMatch);
  const routeGroupId = get(exploreRouteMatch, 'params.groupId', null);
  const ws = get(exploreRouteMatch, 'params.ws', '');

  const backConfig = (() => {
    if (activeTab === 1) return { label: 'Back to Assets', path: `/host/${ws}` };
    if (activeTab === 2) return { label: 'Back to Services', path: `/service/${ws}` };
    return { label: 'Back to Vulnerabilities', path: `/manage/${ws}` };
  })();
  const ActiveTabIcon = (() => {
    if (activeTab === 1) return IconAsset;
    if (activeTab === 2) return IconService;
    return IconVulnerability;
  })();

  const handleBack = () => {
    dispatch(closeExploreGroup());
    if (isExploreRoute && ws) {
      dispatch(redirect(backConfig.path));
    }
  };

  const handleUngroup = () => {
    dispatch(showUngroupConfirmationModal());
  };

  const handleCancelUngroup = () => {
    if (isSubmittingUngroup) return;
    dispatch(hideUngroupConfirmationModal());
  };

  const setActiveTab = (tab) => {
    dispatch(setExploreGroupActiveTab(tab));
  };

  useEffect(() => {
    if (isExploreRoute || isOpen) {
      dispatch(refreshExploreGroupHosts());
      dispatch(refreshExploreGroupServices());
    }
  }, [dispatch, isExploreRoute, isOpen, pathname]);

  const groupDisplayName = get(explorePayload, 'groupDisplayName', '').trim() || 'Explore Group';
  const groupBadgeTitle = get(explorePayload, 'groupBadgeTitle', '').trim();
  const groupId = get(explorePayload, 'groupId', routeGroupId);
  const groupHeaderId = get(explorePayload, 'groupHeaderId', null);
  const payloadVulnCount = get(explorePayload, 'groupVulnsCount', null);
  const groupByCriterionRaw = (get(explorePayload, 'groupByCriterion', '') || '').toString().trim().toLowerCase();
  const isAutomaticGroup = get(explorePayload, 'isAutomaticGroup', null) === true;
  const isManualGroup = get(explorePayload, 'isAutomaticGroup', null) === false;
  const selectedGroupVulns = (vulnsSelected ?? []).filter((vuln) => belongsToGroup(vuln, groupId));
  const selectedGroupMembers = selectedGroupVulns.filter((vuln) => vuln?.is_main !== true);
  const selectedGroupVulnIds = selectedGroupMembers.map(getVulnId).filter((id) => id !== null);
  const selectedGroupVulnNames = selectedGroupMembers.map((vuln) => vuln?.name || `#${getVulnId(vuln)}`);
  let vulnCount = null;
  if ((isExploreRoute || isOpen) && !isManageFetching) vulnCount = manageVulnCount;
  else if (typeof payloadVulnCount === 'number') vulnCount = payloadVulnCount;
  else if (!isManageFetching) vulnCount = manageVulnCount;
  const assetsCount = isHostsFetching ? null : rawAssetsCount;
  const servicesCount = isServicesFetching ? null : rawServicesCount;
  const criterionLabel = groupByCriterionRaw
    ? (GROUP_BY_LABELS[groupByCriterionRaw] ?? groupByCriterionRaw.toUpperCase())
    : '';
  const resolvedGroupBadgeTitle = (() => {
    if (!groupBadgeTitle) return '';
    if (!isAutomaticGroup) return groupBadgeTitle;
    if (criterionLabel) return `${criterionLabel}`;
    if (!groupDisplayName || groupDisplayName === groupBadgeTitle) return groupBadgeTitle;
    return `${groupDisplayName} | ${groupBadgeTitle}`;
  })();

  const handleConfirmUngroup = async () => {
    if (isSubmittingUngroup) return;

    const targetGroupId = groupId ?? groupHeaderId;
    if (targetGroupId === null || typeof targetGroupId === 'undefined') {
      dispatch(hideUngroupConfirmationModal());
      return;
    }

    setIsSubmittingUngroup(true);
    try {
      if (selectedGroupVulnIds.length > 0) {
        const response = await Promise.resolve(dispatch(removeFromManualGroup('vulns', selectedGroupVulnIds)));
        if (response === null) return;

        dispatch(unSelectAll());
        dispatch(hideUngroupConfirmationModal());
        await Promise.all([
          Promise.resolve(dispatch(refreshExploreGroupHosts())),
          Promise.resolve(dispatch(refreshExploreGroupServices()))
        ]);
        return;
      }

      const response = await Promise.resolve(dispatch(deleteManualGroups('vulns', [targetGroupId])));
      if (response === null) return;

      dispatch(hideUngroupConfirmationModal());
      dispatch(closeExploreGroup());
      if (isExploreRoute && ws) {
        dispatch(redirect(`/manage/${ws}`));
      }
    } finally {
      setIsSubmittingUngroup(false);
    }
  };

  if (!isOpen && !isExploreRoute) return null;

  return (
    <Wrapper>
      <UngroupConfirmationModal
        show={ showUngroupModal }
        groupName={ groupBadgeTitle || groupDisplayName }
        selectedVulnNames={ selectedGroupVulnNames }
        loading={ isSubmittingUngroup }
        onCancel={ handleCancelUngroup }
        onConfirm={ handleConfirmUngroup }
      />
      <Left>
        <Button icon={ <Prev small={ false } /> } tooltip={ backConfig.label } onClick={ handleBack } small={ false } />
        <ActiveTabIcon />
        <Separator />
        <FolderIcon />
        <Title>{groupDisplayName}</Title>
        {!!resolvedGroupBadgeTitle && <GroupBadge title={ resolvedGroupBadgeTitle } />}
        {isManualGroup && (
          <Button icon={ <Ungroup /> } tooltip={ 'Ungroup' } onClick={ handleUngroup } small={ false } />
        )}
        <Separator />
        <ExploreTabs
          activeTab={ activeTab }
          setActiveTab={ setActiveTab }
          vulnCount={ vulnCount }
          assetsCount={ assetsCount }
          servicesCount={ servicesCount }
        />
      </Left>
    </Wrapper>
  );
};

export default ExploreGroupBar;
